import {
  Column,
  Flex,
  HorizontalLine,
  Row,
} from "@faire/web--source/slate/Layout";
import { LoadingSkeleton } from "@faire/web--source/slate/LoadingSkeleton";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React from "react";
import styled from "styled-components";

import { LoadingContainer } from "./styled";

export const ProductLoadingSkeleton: React.FC = () => {
  const { isTabletAndAbove, isDesktopAndAbove } = useViewport();

  const headerSkeleton = isDesktopAndAbove ? (
    <StickyBrandHeaderSkeleton />
  ) : (
    <BreadcrumbSkeleton />
  );
  return (
    <Wrapper>
      <StyledLoadingContainer>
        <Column>
          {isTabletAndAbove ? <>{headerSkeleton}</> : null}
          <RowMobileColumn>
            {/* Image Placeholder */}
            <Column flex={1}>
              <ImageGridContainer>
                <SquareLoadingSkeleton $numLabel={0} />
                {isTabletAndAbove ? (
                  <>
                    <SquareLoadingSkeleton $numLabel={1} />
                    <SquareLoadingSkeleton $numLabel={2} />
                    <SquareLoadingSkeleton $numLabel={3} />
                  </>
                ) : null}
              </ImageGridContainer>
            </Column>
            <Spacer
              width={useResponsiveValue({
                mobileAndAbove: "2x",
                tabletAndAbove: "5x",
              })}
            />
            {/* Product details Placeholder */}
            <ProductDetailsContainer>
              {!isDesktopAndAbove ? (
                <>
                  <Row align="center">
                    <LoadingSkeleton
                      width="40px"
                      height="40px"
                      borderRadius="50%"
                    />
                    <Spacer width="1x" />
                    <LoadingSkeleton width="160px" height="20px" />
                  </Row>
                  <Spacer height="3x" />
                </>
              ) : null}
              {/* Product Name */}
              <LoadingSkeleton width="100%" height="32px" />
              <Spacer height="2x" />
              {/* Product Price */}
              <LoadingSkeleton width="100%" height="24px" />
              <Spacer height="3x" />
              {/* Quantity Dropdown  */}
              <Row justify="space-between">
                <LoadingSkeleton width="64px" height="8px" />
                <LoadingSkeleton width="64px" height="8px" />
              </Row>
              <Spacer height={4} />
              <LoadingSkeleton width="100%" height="40px" />
              <Spacer height="2x" />
              {/* Add to Bag CTA */}
              <LoadingSkeleton width="100%" height="40px" />
              <Spacer height="8x" />
              <LoadingSkeleton width="64px" height="8px" />
              <Spacer height="2x" />
              <LoadingSkeleton width="200px" height="8px" />
            </ProductDetailsContainer>
          </RowMobileColumn>
        </Column>
      </StyledLoadingContainer>
    </Wrapper>
  );
};

const StickyBrandHeaderSkeleton = () => {
  return (
    <>
      <Spacer height={12} />
      <Row align="center">
        <LoadingSkeleton width="40px" height="40px" borderRadius="50%" />
        <Spacer width="1x" />
        <LoadingSkeleton width="200px" height="20px" />
      </Row>
      <Spacer height={12} />
    </>
  );
};

const BreadcrumbSkeleton = () => {
  return (
    <>
      <Spacer height={12} />
      {/* Breadcrumbs */}
      <LoadingSkeleton width="400px" height="20px" />
      <Spacer height={12} />
      <HorizontalLine />
      <Spacer height="5x" />
    </>
  );
};

const Wrapper = styled(Column).attrs({ align: "center" })`
  width: 100%;
`;

const StyledLoadingContainer = styled(LoadingContainer)`
  max-width: 1280px;
  padding: ${getSpacing(0, "6x")};
  ${responsiveMediaQueries.mobile} {
    padding: 0;
  }
`;

const ProductDetailsContainer = styled(Column)`
  width: 360px;

  ${responsiveMediaQueries.tablet} {
    width: 300px;
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "2x")};
    width: 100%;
  }
`;

const ImageGridContainer = styled.div`
  display: grid;
  grid-template:
    "skeleton-0 skeleton-1" auto
    "skeleton-2 skeleton-3" auto;
  gap: 4px;

  ${responsiveMediaQueries.tablet} {
    grid-template:
      "skeleton-0 skeleton-0" auto
      "skeleton-1 skeleton-2" auto
      "skeleton-3 skeleton-3" auto;
  }

  ${responsiveMediaQueries.mobile} {
    display: flex;
  }
`;

const SquareLoadingSkeleton = styled(LoadingSkeleton).attrs({
  width: "100%",
  height: "100%",
})<{ $numLabel: number }>`
  aspect-ratio: 1/1;
  grid-area: ${(props) => `skeleton-${props.$numLabel}`};
`;

const RowMobileColumn = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})``;
